import React, { useEffect, useState } from "react";
import axiosWrapper from "../../utils/AxiosWrapper";
import { toast } from "react-hot-toast";

const tabs = [
  { id: "practice", label: "Fundamentals practice" },
  { id: "leaderboard", label: "Branch leaderboard" },
];

function blockedHintFromError(e) {
  if (e?.response?.status !== 403) return null;
  const body = e.response?.data || {};
  const extra = body.data;
  if (extra?.reason === "NOT_B_ED_BRANCH") {
    return extra.hint || body.message || "Your branch must be marked as B.Ed.";
  }
  return body.message || "Access denied.";
}

const BedPracticeHub = () => {
  const token = localStorage.getItem("userToken");
  const headers = { Authorization: `Bearer ${token}` };

  const [tab, setTab] = useState("practice");
  const [sets, setSets] = useState([]);
  const [accessHint, setAccessHint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quiz, setQuiz] = useState(null);
  const [picked, setPicked] = useState({});
  const [startedAt, setStartedAt] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [board, setBoard] = useState(null);
  const [lbLoading, setLbLoading] = useState(false);

  const loadSets = async () => {
    setLoading(true);
    try {
      const res = await axiosWrapper.get("/bed-practice/student/sets", {
        headers,
      });
      if (res.data.success) {
        setAccessHint(null);
        setSets(res.data.data || []);
      } else toast.error(res.data.message);
    } catch (e) {
      const hint = blockedHintFromError(e);
      if (hint) {
        setAccessHint(hint);
        setSets([]);
      } else {
        toast.error(e.response?.data?.message || "Could not load practice sets");
      }
    } finally {
      setLoading(false);
    }
  };

  const loadLeaderboard = async () => {
    setLbLoading(true);
    try {
      const res = await axiosWrapper.get("/bed-practice/student/leaderboard", {
        headers,
      });
      if (res.data.success) {
        setAccessHint(null);
        setBoard(res.data.data);
      } else toast.error(res.data.message);
    } catch (e) {
      const hint = blockedHintFromError(e);
      if (hint) {
        setAccessHint(hint);
        setBoard(null);
      } else {
        toast.error(e.response?.data?.message || "Leaderboard unavailable");
      }
    } finally {
      setLbLoading(false);
    }
  };

  useEffect(() => {
    loadSets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tab === "leaderboard") loadLeaderboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const openSet = async (id) => {
    setLastResult(null);
    setPicked({});
    setStartedAt(Date.now());
    try {
      const res = await axiosWrapper.get(`/bed-practice/student/sets/${id}`, {
        headers,
      });
      if (res.data.success) {
        setQuiz(res.data.data);
      } else toast.error(res.data.message);
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not open set");
    }
  };

  const closeQuiz = () => {
    setQuiz(null);
    setPicked({});
    setStartedAt(null);
    setLastResult(null);
  };

  const toggleChoice = (qIndex, key) => {
    setPicked((prev) => ({ ...prev, [qIndex]: key }));
  };

  const submitQuiz = async () => {
    if (!quiz?.questions?.length) return;
    const missing = quiz.questions.find((_, i) => !picked[i]);
    if (missing !== undefined) {
      toast.error("Answer every question before submitting.");
      return;
    }
    const durationSec = startedAt ? Math.round((Date.now() - startedAt) / 1000) : 0;
    const responses = quiz.questions.map((_, i) => ({
      index: i,
      key: picked[i],
    }));
    setSubmitting(true);
    try {
      const res = await axiosWrapper.post(
        "/bed-practice/student/attempts",
        { setId: quiz._id, responses, durationSec },
        { headers }
      );
      if (res.data.success) {
        setLastResult(res.data.data);
        toast.success(
          `Score ${res.data.data.score} / ${res.data.data.maxScore}`
        );
      } else toast.error(res.data.message);
    } catch (e) {
      toast.error(e.response?.data?.message || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (quiz) {
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-slate-900">{quiz.title}</h2>
            {quiz.weekLabel ? (
              <p className="text-sm text-slate-500 mt-0.5">{quiz.weekLabel}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={closeQuiz}
            className="text-sm font-semibold text-indigo-600 hover:text-indigo-800"
          >
            Back to list
          </button>
        </div>

        {lastResult ? (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50/80 px-4 py-3 text-emerald-900">
            <p className="font-semibold">
              You scored {lastResult.score} / {lastResult.maxScore} (
              {lastResult.percent}%)
            </p>
            <p className="text-sm mt-1 text-emerald-800">
              XP from this round is added to your branch leaderboard.
            </p>
          </div>
        ) : null}

        <ol className="space-y-6">
          {quiz.questions.map((q) => (
            <li
              key={q.index}
              className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm"
            >
              <p className="font-medium text-slate-900">
                <span className="text-slate-400 mr-2">{q.index + 1}.</span>
                {q.prompt}
              </p>
              {q.hint ? (
                <p className="text-xs text-slate-500 mt-1 italic">Hint: {q.hint}</p>
              ) : null}
              <ul className="mt-3 space-y-2">
                {q.choices.map((c) => {
                  const sel = picked[q.index] === c.key;
                  const fb = lastResult?.breakdown?.find((b) => b.index === q.index);
                  const showWrong = fb && !fb.correct && sel;
                  return (
                    <li key={c.key}>
                      <button
                        type="button"
                        disabled={!!lastResult}
                        onClick={() => toggleChoice(q.index, c.key)}
                        className={`w-full text-left rounded-xl px-3 py-2.5 text-sm border transition ${
                          sel
                            ? showWrong
                              ? "border-rose-300 bg-rose-50"
                              : "border-indigo-500 bg-indigo-50"
                            : "border-slate-200 hover:border-slate-300 bg-slate-50/50"
                        }`}
                      >
                        <span className="font-semibold text-slate-600 mr-2">
                          {c.key}.
                        </span>
                        {c.text}
                      </button>
                    </li>
                  );
                })}
              </ul>
              {lastResult &&
                (() => {
                  const fb = lastResult.breakdown?.find((b) => b.index === q.index);
                  if (!fb || fb.correct) return null;
                  return (
                    <p className="mt-2 text-sm text-rose-700">
                      {fb.explanation || `Correct answer: ${fb.correctKey}`}
                    </p>
                  );
                })()}
            </li>
          ))}
        </ol>

        {!lastResult ? (
          <div className="flex justify-end">
            <button
              type="button"
              disabled={submitting}
              onClick={submitQuiz}
              className="rounded-xl bg-slate-900 text-white px-6 py-3 text-sm font-semibold disabled:opacity-50"
            >
              {submitting ? "Submitting…" : "Submit answers"}
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-3 justify-end">
            <button
              type="button"
              onClick={() => {
                setLastResult(null);
                setPicked({});
                setStartedAt(Date.now());
              }}
              className="rounded-xl border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-800"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={closeQuiz}
              className="rounded-xl bg-indigo-600 text-white px-5 py-2.5 text-sm font-semibold"
            >
              Done
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">
          B.Ed fundamentals
        </h1>
        <p className="text-slate-600 text-sm mt-1">
          Practice last-class ideas and compete on XP with classmates in your
          branch only.
        </p>
      </header>

      {accessHint ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-4 text-sm text-rose-950 space-y-2">
          <p className="font-bold">You cannot open sets or leaderboard</p>
          <p className="text-rose-900/95 leading-snug">{accessHint}</p>
        </div>
      ) : null}

      <div className="flex gap-2 p-1 bg-slate-200/60 rounded-xl w-fit">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
              tab === t.id
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "practice" ? (
        <div>
          {accessHint ? (
            <p className="text-slate-500 text-sm">
              Fix branch settings above to load quizzes.
            </p>
          ) : loading ? (
            <p className="text-slate-500 text-sm">Loading sets…</p>
          ) : sets.length === 0 ? (
            <div className="rounded-2xl border border-amber-200/80 bg-amber-50/90 px-4 py-4 text-sm text-amber-950 space-y-2">
              <p className="font-semibold">No sets match your profile yet</p>
              <p className="text-amber-900/90">
                Ask admin to publish a set that uses{" "}
                <strong>semester 0 (all semesters)</strong> or matches your{" "}
                <strong>current semester</strong>, and either{" "}
                <strong>all branches</strong> or your branch. Sets also need{" "}
                <strong>Active</strong> turned on.
              </p>
            </div>
          ) : (
            <ul className="grid gap-3 sm:grid-cols-2">
              {sets.map((s) => (
                <li key={s._id}>
                  <button
                    type="button"
                    onClick={() => openSet(s._id)}
                    className="w-full text-left rounded-2xl border border-slate-200 bg-white p-4 shadow-sm hover:border-indigo-200 hover:shadow transition"
                  >
                    <p className="font-semibold text-slate-900">{s.title}</p>
                    {s.weekLabel ? (
                      <p className="text-xs text-indigo-600 mt-1">{s.weekLabel}</p>
                    ) : null}
                    {s.description ? (
                      <p className="text-sm text-slate-600 mt-2 line-clamp-2">
                        {s.description}
                      </p>
                    ) : null}
                    <p className="text-xs text-slate-400 mt-3">
                      {s.questionCount} questions
                      {Number(s.semester) === 0
                        ? " · All semesters"
                        : ` · Semester ${s.semester}`}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          {accessHint ? (
            <p className="p-6 text-slate-500 text-sm">
              Leaderboard unlocks once your branch is B.Ed-enabled (see notice
              above).
            </p>
          ) : lbLoading ? (
            <p className="p-6 text-slate-500 text-sm">Loading leaderboard…</p>
          ) : board ? (
            <>
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                <p className="text-sm font-semibold text-slate-800">
                  {board.branchName || "Your branch"}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  B.Ed students only · Top 50 by total XP from practice attempts
                </p>
              </div>
              {board.you ? (
                <div className="px-4 py-2 bg-indigo-50 border-b border-indigo-100 text-sm">
                  <span className="font-semibold text-indigo-900">You: </span>
                  rank {board.you.rank}, {board.you.totalXp} XP (
                  {board.you.attempts} attempts)
                </div>
              ) : null}
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-slate-100 text-left text-slate-600">
                      <th className="px-4 py-2 font-semibold">#</th>
                      <th className="px-4 py-2 font-semibold">Student</th>
                      <th className="px-4 py-2 font-semibold">Enrollment</th>
                      <th className="px-4 py-2 font-semibold">XP</th>
                      <th className="px-4 py-2 font-semibold">Attempts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(board.leaderboard || []).length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-4 py-8 text-center text-slate-500"
                        >
                          No attempts yet. Be the first to practise.
                        </td>
                      </tr>
                    ) : (
                      (board.leaderboard || []).map((row) => (
                        <tr
                          key={`${row.rank}-${row.enrollmentNo}`}
                          className="border-t border-slate-100"
                        >
                          <td className="px-4 py-2 font-medium">{row.rank}</td>
                          <td className="px-4 py-2">
                            {row.firstName} {row.lastName}
                          </td>
                          <td className="px-4 py-2 text-slate-600">
                            {row.enrollmentNo}
                          </td>
                          <td className="px-4 py-2">{row.totalXp}</td>
                          <td className="px-4 py-2">{row.attempts}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <p className="p-6 text-slate-500 text-sm">No data.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default BedPracticeHub;
